import Button from "components/atoms/Button";
import InputCheckbox from "components/atoms/InputCheckbox";
import InputDate from "components/atoms/InputDate";
import { useFormik } from "formik";
import moment from "moment";
import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  errorToastNotif,
  getDataFromLocalStorage,
  nullConverter,
  successToastNotif,
} from "utils/common";
import {
  LarvacideTypeOptions,
  LarvacideFormOptions,
  PageMode,
  UnitOptions,
  YesAndNoOptions,
  BrackishWaterOptions,
  KohortActivityTypeOptions,
  TraitBreedingOptions,
  StatusTpOptions,
  TpGoneReasonOptions,
} from "utils/constant";
import {
  createEmptyLarvicideEntry,
  cleanLarvicideEntries,
} from "../utils/larvicide";
import { useParams } from "react-router-dom";
import LarvaBreeding from "services/LarvaBreeding";
import InputField from "components/atoms/InputField";
import InputSelect from "components/atoms/InputSelect";
import { BreadcrumbNav } from "components/molecules";
import MapsComponents from "components/Maps/OpenStreetMap";
import Dropzone from "react-dropzone";
import { toast } from "react-toastify";

const LarvaBreedingService = new LarvaBreeding();

export const actualInterventionValues = [
  "Larvasidasi",
  "Penebaran Ikan",
  "Modifikasi Lingkungan",
  "Manipulasi Lingkungan",
  "Lainnya",
];

export const checkHasIntervention = (cohort) => {
  if (!cohort) return false;
  let interventions = cohort.intervention_done;
  if (typeof interventions === "string") {
    try {
      interventions = JSON.parse(interventions);
    } catch (e) {
      interventions = [interventions];
    }
  }
  if (!Array.isArray(interventions)) return false;

  return interventions.some((item) => {
    if (typeof item !== "string") return false;
    if (actualInterventionValues.includes(item)) return true;
    if (item.startsWith("Lainnya_")) return true;
    return false;
  });
};

export const parseInterventionData = (item) => {
  let otherIntervention = "";
  let otherBelum = "";

  let rawInterventions = item?.intervention_done || [];
  if (typeof rawInterventions === "string") {
    try {
      rawInterventions = JSON.parse(rawInterventions);
    } catch (e) {
      rawInterventions = [rawInterventions];
    }
  }
  if (!Array.isArray(rawInterventions)) {
    rawInterventions = [];
  }

  const parsedInterventions = rawInterventions.map((intervention) => {
    if (
      typeof intervention === "string" &&
      intervention.startsWith("Lainnya_")
    ) {
      otherIntervention = intervention.substring(8);
      return "Lainnya";
    }
    if (
      typeof intervention === "string" &&
      intervention.startsWith("Belum_")
    ) {
      otherBelum = intervention.substring(6);
      return "Belum dilakukan Intervensi";
    }
    return intervention;
  });

  let initialLarvicideEntries = [];
  let rawEntries = item?.larvicide_entries;
  if (typeof rawEntries === "string") {
    try {
      rawEntries = JSON.parse(rawEntries);
    } catch (e) {
      rawEntries = null;
    }
  }

  if (Array.isArray(rawEntries) && rawEntries.length > 0) {
    initialLarvicideEntries = rawEntries.map((entry) => ({
      type: entry?.type
        ? entry.type === "BenzoylUreas"
          ? "Benzoilurea"
          : entry.type
        : "",
      form: entry?.form || "",
      form_other: entry?.form_other || "",
      amount:
        entry?.amount != null && entry?.amount !== ""
          ? String(entry.amount)
          : "",
      unit: entry?.unit || "",
    }));
  } else if (
    item?.type_larvicide ||
    item?.total_larvicide != null ||
    item?.total_larvicide_unit
  ) {
    initialLarvicideEntries = [
      {
        type: item.type_larvicide
          ? item.type_larvicide === "BenzoylUreas"
            ? "Benzoilurea"
            : item.type_larvicide
          : "",
        form: "",
        form_other: "",
        amount:
          item.total_larvicide != null
            ? String(item.total_larvicide)
            : "",
        unit: item.total_larvicide_unit || "",
      },
    ];
  }

  return {
    intervention_done: parsedInterventions,
    other_intervention_done: otherIntervention,
    other_belum_intervention: otherBelum,
    larvicide_entries: initialLarvicideEntries,
  };
};

export const getPreviousKohort = (
  cohortList,
  currentId,
  currentDate,
  pageMode
) => {
  if (!cohortList || cohortList.length === 0) return null;

  const candidates = cohortList.filter(
    (k) => !currentId || String(k.id) !== String(currentId)
  );
  if (candidates.length === 0) return null;

  const sortFn = (a, b) => {
    const dateA = new Date(a.intervention_date || 0);
    const dateB = new Date(b.intervention_date || 0);
    const dateDiff = dateB - dateA;
    if (dateDiff !== 0) return dateDiff;
    const createdA = new Date(a.createdAt || 0);
    const createdB = new Date(b.createdAt || 0);
    return createdB - createdA;
  };

  if (!currentDate || !moment(currentDate).isValid()) {
    if (pageMode === PageMode.add) {
      const sorted = [...candidates].sort(sortFn);
      return sorted[0] || null;
    }
    return null;
  }

  const curDateStr = moment(currentDate).format("YYYY-MM-DD");
  const strictlyBefore = candidates.filter((k) => {
    if (!k.intervention_date) return false;
    const kDateStr = moment(k.intervention_date).format("YYYY-MM-DD");
    return kDateStr < curDateStr;
  });

  if (strictlyBefore.length === 0) return null;

  strictlyBefore.sort(sortFn);
  return strictlyBefore[0] || null;
};

const InterventionDoneOptions = [
  { label: "Larvasidasi", value: "Larvasidasi" },
  { label: "Penebaran Ikan Pemakan Larva", value: "Penebaran Ikan" },
  {
    label:
      "Modifikasi lingkungan (Penimbunan, Pengeringan, Pembuatan Tanggul dll)",
    value: "Modifikasi Lingkungan",
  },
  {
    label:
      "Manipulasi Lingkungan (Pembersihan Lumut, Mengubah Kadar Garam dll)",
    value: "Manipulasi Lingkungan",
  },
  { label: "Intervensi lainnya", value: "Lainnya" },
  {
    label: "Belum Dilakukan Intervensi",
    value: "Belum dilakukan Intervensi",
  },
];

const FormComponent = ({ changeMode, pageMode }) => {
  const [itemToEdit, setItemToEdit] = useState(() => {
    if (pageMode === PageMode.edit) {
      try {
        return JSON.parse(getDataFromLocalStorage("SELECTED_ITEM"));
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [cohortList, setCohortList] = useState([]);
  const [cohortListLoaded, setCohortListLoaded] = useState(false);
  const [filesImage, setFilesImage] = useState([]);
  const [filesImageLarva, setFilesImageLarva] = useState([]);
  const [imagePreview, setImagePreview] = useState("");
  const [imageLarvaPreview, setImageLarvaPreview] = useState("");
  const [modalImage, setModalImage] = useState("");

  const { currentUser } = useSelector((state) => state.authReducer);
  const params = useParams();

  const formik = useFormik({
    initialValues: {
      intervention_date: "",
      activity_type: "",
      intervention_done: [],
      other_intervention_done: "",
      other_belum_intervention: "",
      larvicide_entries: [],
      officer_team: "",
      anopheles_larva_found: "",
      total_catch: "",
      total_larva: "",
      larva_phase: [],
      area_breeding_place: "",
      depth_breeding_place: "",
      distance_to_nearest_house: "",
      water_turbidity: "",
      plan_exist_breeding_place: "",
      rubbish_exist_breeding_place: "",
      trait_breeding_place: "",
      sun_exposure: "",
      brackish_water: "",
      running_water: "",
      fish_exist: "",
      coordinate: "",
      status: "",
      tp_gone_reason: "",
    },
    validate: (values) => {
      const errors = {};

      if (!values.intervention_date) {
        errors.intervention_date = "Tanggal Intervensi wajib diisi";
      }

      if (!values.officer_team) {
        errors.officer_team = "Nama Petugas/Tim wajib diisi";
      }

      if (!values.status) {
        errors.status = "Status Tempat Perindukan wajib diisi";
      }

      if (values.status === "Tidak Ada") {
        if (!values.tp_gone_reason) {
          errors.tp_gone_reason = "Alasan wajib diisi";
        }
      }

      if (values.status === "Masih Ada") {
        const isEmpty = (v) => v === "" || v === null || v === undefined;

        if (isEmpty(values.area_breeding_place)) {
          errors.area_breeding_place = "Luas Tempat Perindukan (m2) wajib diisi";
        }

        if (isEmpty(values.depth_breeding_place)) {
          errors.depth_breeding_place =
            "Kedalaman Tempat Perindukan (m) wajib diisi";
        }

        if (isEmpty(values.distance_to_nearest_house)) {
          errors.distance_to_nearest_house =
            "Jarak Tempat Perindukan Dari Rumah Terdekat (m) wajib diisi";
        }

        if (!values.water_turbidity) {
          errors.water_turbidity = "Kekeruhan Air wajib diisi";
        }

        if (!values.plan_exist_breeding_place) {
          errors.plan_exist_breeding_place =
            "Adanya Tanaman Pada Permukaan Air wajib diisi";
        }

        if (!values.rubbish_exist_breeding_place) {
          errors.rubbish_exist_breeding_place =
            "Adanya Sampah Pada Permukaan Air wajib diisi";
        }

        if (!values.trait_breeding_place) {
          errors.trait_breeding_place = "Sifat Tempat Perindukan wajib diisi";
        }

        if (!values.sun_exposure) {
          errors.sun_exposure = "Paparan Sinar Matahari wajib diisi";
        }

        if (!values.brackish_water) {
          errors.brackish_water = "Air Payau wajib diisi";
        }

        if (!values.running_water) {
          errors.running_water = "Air Mengalir wajib diisi";
        }

        if (!values.fish_exist) {
          errors.fish_exist = "Ada Ikan wajib diisi";
        }

        const prevKohort = getPreviousKohort(
          cohortList,
          itemToEdit?.id,
          values.intervention_date,
          pageMode
        );
        if (checkHasIntervention(prevKohort) && !values.activity_type) {
          errors.activity_type = "Jenis Kegiatan wajib diisi";
        }

        if (!values.anopheles_larva_found) {
          errors.anopheles_larva_found = "Ditemukan Jentik Anopheles wajib diisi";
        }

        if (
          values.total_catch === "" ||
          values.total_catch === null ||
          values.total_catch === undefined
        ) {
          errors.total_catch = "Jumlah Cidukan Yang Dilakukan wajib diisi";
        }

        if (
          values.total_larva === "" ||
          values.total_larva === null ||
          values.total_larva === undefined
        ) {
          errors.total_larva =
            "Jumlah Keseluruhan Jentik Dalam Seluruh Cidukan wajib diisi";
        }

        if (!values.intervention_done || values.intervention_done.length === 0) {
          errors.intervention_done =
            "Intervensi yang telah dilakukan wajib dipilih setidaknya satu";
        }

        if (values.intervention_done?.includes("Larvasidasi")) {
          const entriesErrors = [];
          let hasEntriesErrors = false;

          (values.larvicide_entries || []).forEach((entry, index) => {
            const rowErrors = {};
            if (pageMode === PageMode.add) {
              if (!entry.type) {
                rowErrors.type = "Jenis Larvasida wajib diisi";
              }
              if (!entry.form) {
                rowErrors.form = "Bentuk Larvasida wajib diisi";
              }
              if (
                entry.amount === "" ||
                entry.amount === null ||
                entry.amount === undefined
              ) {
                rowErrors.amount = "Jumlah Larvasida wajib diisi";
              }
              if (!entry.unit) {
                rowErrors.unit = "Satuan wajib diisi";
              }
            }

            if (
              entry.form === "Lainnya" &&
              (!entry.form_other || !entry.form_other.trim())
            ) {
              rowErrors.form_other = "Bentuk Lainnya wajib diisi";
            }

            if (Object.keys(rowErrors).length > 0) {
              hasEntriesErrors = true;
              entriesErrors[index] = rowErrors;
            }
          });

          if (hasEntriesErrors) {
            errors.larvicide_entries = entriesErrors;
          }
        }
      }

      return errors;
    },
    onSubmit: async (values) => {
      const mappedIntervention = (values.intervention_done || []).map((item) => {
        if (item === "Lainnya") {
          return `Lainnya_${values.other_intervention_done}`;
        }
        if (item === "Belum dilakukan Intervensi") {
          return `Belum_${values.other_belum_intervention}`;
        }
        return item;
      });

      const cleanedEntries = values.intervention_done?.includes("Larvasidasi")
        ? cleanLarvicideEntries(values.larvicide_entries)
        : [];

      const payload = {
        vector_larva_identification_id: params.id,
        intervention_date: nullConverter(values.intervention_date),
        activity_type: nullConverter(values.activity_type),
        intervention_done: mappedIntervention,
        larvicide_entries: cleanedEntries,
        officer_team: nullConverter(values.officer_team),
        anopheles_larva_found: nullConverter(values.anopheles_larva_found),
        total_catch: nullConverter(values.total_catch),
        total_larva: nullConverter(values.total_larva),
        larva_phase: values.larva_phase,
        area_breeding_place: nullConverter(values.area_breeding_place),
        depth_breeding_place: nullConverter(values.depth_breeding_place),
        distance_to_nearest_house: nullConverter(values.distance_to_nearest_house),
        water_turbidity: nullConverter(values.water_turbidity),
        plan_exist_breeding_place: nullConverter(values.plan_exist_breeding_place),
        rubbish_exist_breeding_place: nullConverter(values.rubbish_exist_breeding_place),
        trait_breeding_place: nullConverter(values.trait_breeding_place),
        sun_exposure: nullConverter(values.sun_exposure),
        brackish_water: nullConverter(values.brackish_water),
        running_water: nullConverter(values.running_water),
        fish_exist: nullConverter(values.fish_exist),
        coordinate: nullConverter(values.coordinate),
        status: nullConverter(values.status),
        tp_gone_reason: nullConverter(values.tp_gone_reason),
        image: filesImage[0] || null,
        image_larva:
          values.anopheles_larva_found === "Tidak"
            ? null
            : filesImageLarva[0] || null,
      };

      switch (pageMode) {
        case PageMode.add:
          await LarvaBreedingService.add(payload)
            .then((res) => {
              changeMode(PageMode.view);
              successToastNotif(
                "Data Kohort berhasil ditambahkan"
              );
            })
            .catch((err) => {
              console.log(err);
              errorToastNotif(err);
            });
          break;
        case PageMode.edit:
          await LarvaBreedingService.update(payload, itemToEdit?.id)
            .then((res) => {
              changeMode(PageMode.view);
              successToastNotif(
                "Data Kohort berhasil diupdate"
              );
            })
            .catch((err) => {
              console.log(err);
              errorToastNotif(err);
            });
          break;
        default:
          break;
      }
    },
  });

  const handleCheckboxChange = (e, field) => {
    const isChecked = e.target.checked;
    const value = e.target.value;

    const newValues = isChecked
      ? [...formik.values[field], value]
      : formik.values[field].filter((item) => item !== value);

    if (field === "intervention_done") {
      let updatedEntries = formik.values.larvicide_entries || [];
      if (value === "Larvasidasi") {
        if (isChecked) {
          if (updatedEntries.length === 0) {
            updatedEntries = [createEmptyLarvicideEntry()];
          }
        } else {
          updatedEntries = [];
        }
      }
      formik.setValues({
        ...formik.values,
        [field]: newValues,
        larvicide_entries: updatedEntries,
      });
      return;
    }

    formik.setFieldValue(field, newValues);
  };

  const handleAddLarvicideRow = () => {
    formik.setFieldValue("larvicide_entries", [
      ...(formik.values.larvicide_entries || []),
      createEmptyLarvicideEntry(),
    ]);
  };

  const handleRemoveLarvicideRow = (index) => {
    const current = formik.values.larvicide_entries || [];
    const next = current.filter((_, i) => i !== index);
    formik.setFieldValue("larvicide_entries", next);
  };

  const handleLarvicideEntryChange = (index, field, value) => {
    const current = [...(formik.values.larvicide_entries || [])];
    if (!current[index]) return;

    if (field === "form") {
      current[index] = {
        ...current[index],
        form: value,
        form_other: value === "Lainnya" ? current[index].form_other : "",
      };
    } else {
      current[index] = {
        ...current[index],
        [field]: value,
      };
    }
    formik.setFieldValue("larvicide_entries", current);
  };

  const handleMapChange = (address, coordinate) => {
    const coordinateValue = Object.values(coordinate).toString();
    formik.setFieldValue("coordinate", coordinateValue);
  };

  const previousKohort = useMemo(
    () =>
      getPreviousKohort(
        cohortList,
        itemToEdit?.id,
        formik.values.intervention_date,
        pageMode
      ),
    [cohortList, itemToEdit?.id, formik.values.intervention_date, pageMode]
  );

  const showActivityType =
    formik.values.status === "Masih Ada" &&
    checkHasIntervention(previousKohort);

  useEffect(() => {
    if (
      cohortListLoaded &&
      !showActivityType &&
      formik.values.activity_type !== ""
    ) {
      formik.setFieldValue("activity_type", "");
    }
  }, [cohortListLoaded, showActivityType, formik.values.activity_type]);

  const handleActivityTypeChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    if (value === "Pasca Intervensi") {
      if (previousKohort) {
        const parsed = parseInterventionData(previousKohort);
        formik.setValues({
          ...formik.values,
          activity_type: value,
          intervention_done: parsed.intervention_done,
          other_intervention_done: parsed.other_intervention_done,
          other_belum_intervention: parsed.other_belum_intervention,
          larvicide_entries: parsed.larvicide_entries,
        });
        return;
      }
    } else if (value === "Intervensi Ulang") {
      formik.setValues({
        ...formik.values,
        activity_type: value,
        intervention_done: [],
        other_intervention_done: "",
        other_belum_intervention: "",
        larvicide_entries: [],
      });
      return;
    }
    formik.setFieldValue("activity_type", value);
  };

  useEffect(() => {
    if (pageMode === PageMode.edit) {
      const selectedItem = JSON.parse(getDataFromLocalStorage("SELECTED_ITEM"));
      setItemToEdit(selectedItem);

      const parsed = parseInterventionData(selectedItem);

      formik.setValues({
        intervention_date: selectedItem?.intervention_date,
        activity_type: selectedItem?.activity_type || "",
        intervention_done: parsed.intervention_done,
        other_intervention_done: parsed.other_intervention_done,
        other_belum_intervention: parsed.other_belum_intervention,
        larvicide_entries: parsed.larvicide_entries,
        officer_team: selectedItem?.officer_team || "",
        anopheles_larva_found: selectedItem?.anopheles_larva_found || "",
        // Use nullish coalescing so a legitimate 0 (e.g. total_larva when Anopheles = "Tidak") is preserved on edit instead of becoming an empty string.
        total_catch: selectedItem?.total_catch ?? "",
        total_larva: selectedItem?.total_larva ?? "",
        larva_phase: selectedItem?.larva_phase || [],
        area_breeding_place: selectedItem?.area_breeding_place ?? "",
        depth_breeding_place: selectedItem?.depth_breeding_place ?? "",
        distance_to_nearest_house: selectedItem?.distance_to_nearest_house ?? "",
        water_turbidity: selectedItem?.water_turbidity || "",
        plan_exist_breeding_place: selectedItem?.plan_exist_breeding_place || "",
        rubbish_exist_breeding_place: selectedItem?.rubbish_exist_breeding_place || "",
        trait_breeding_place: selectedItem?.trait_breeding_place || "",
        sun_exposure: selectedItem?.sun_exposure || "",
        brackish_water: selectedItem?.brackish_water || "",
        running_water: selectedItem?.running_water || "",
        fish_exist: selectedItem?.fish_exist || "",
        coordinate: selectedItem?.coordinate || "",
        status: selectedItem?.status || "",
        tp_gone_reason: selectedItem?.tp_gone_reason || "",
      });

      // Load preview images in edit mode if available
      if (selectedItem?.image) {
        LarvaBreedingService.getImage(selectedItem.id)
          .then((blob) => setImagePreview(URL.createObjectURL(blob)))
          .catch(() => {});
      }

      if (selectedItem?.image_larva) {
        LarvaBreedingService.getImageLarva(selectedItem.id)
          .then((blob) => setImageLarvaPreview(URL.createObjectURL(blob)))
          .catch(() => {});
      }
    }
  }, []);

  useEffect(() => {
    if (params.id) {
      const fetchKohorts = async () => {
        try {
          const response = await LarvaBreedingService.getAll({
            searchBy: "vector_larva_identification_id",
            searchValue: params.id,
          });
          const list = response?.vector_larva_interventions || [];
          setCohortList(list);
          setCohortListLoaded(true);

          if (pageMode === PageMode.add && list.length > 0) {
            const sortedList = [...list].sort((a, b) => {
              const dateA = new Date(a.intervention_date || 0);
              const dateB = new Date(b.intervention_date || 0);
              const dateDiff = dateB - dateA;
              if (dateDiff !== 0) {
                return dateDiff;
              }
              const createdA = new Date(a.createdAt || 0);
              const createdB = new Date(b.createdAt || 0);
              return createdB - createdA;
            });
            const lastCohort = sortedList[0];
            if (lastCohort) {
              if (lastCohort.area_breeding_place != null) {
                formik.setFieldValue(
                  "area_breeding_place",
                  String(lastCohort.area_breeding_place)
                );
              }
              if (lastCohort.depth_breeding_place != null) {
                formik.setFieldValue(
                  "depth_breeding_place",
                  String(lastCohort.depth_breeding_place)
                );
              }
              if (lastCohort.distance_to_nearest_house != null) {
                formik.setFieldValue(
                  "distance_to_nearest_house",
                  String(lastCohort.distance_to_nearest_house)
                );
              }
              if (lastCohort.coordinate) {
                formik.setFieldValue(
                  "coordinate",
                  String(lastCohort.coordinate)
                );
              }
            }
          }
        } catch (error) {
          console.log(error);
          setCohortListLoaded(true);
        }
      };

      fetchKohorts();
    }
  }, [pageMode, params.id]);

  const renderBreedingImageDropzone = () => (
    <div className="form-group">
      <label className="image">Ambil Gambar Tempat Perindukan (Opsional)</label>
      <div
        style={{
          display: "flex",
          gap: "16px",
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <div>
          <Dropzone
            maxFiles={1}
            accept="image/*"
            onDrop={(acceptedFiles, fileRejections) => {
              if (fileRejections.length > 0) {
                if (fileRejections[0].errors[0].code === "file-too-large") {
                  toast.error("Ukuran file tidak boleh lebih dari 300 Kb");
                } else if (fileRejections[0].errors[0].code === "file-invalid-type") {
                  toast.error("Format file tidak sesuai");
                }
              }
              if (acceptedFiles.length > 0) {
                setFilesImage(acceptedFiles);
                setImagePreview(URL.createObjectURL(acceptedFiles[0]));
              }
            }}
            maxSize={1024 * 300}
          >
            {({ getRootProps, getInputProps }) => (
              <section
                className="container"
                style={{ maxWidth: "360px", margin: 0, padding: 0 }}
              >
                <div
                  {...getRootProps({
                    className: "dropzone",
                    style: { padding: "24px 16px" },
                  })}
                >
                  <input {...getInputProps()} />
                  <div className="dz-message">
                    <div className="icon">
                      <i className="flaticon-file"></i>
                    </div>
                    <h5>Upload gambar perindukan</h5>
                    <div className="note">Drag & Drop or</div>
                    <button type="button" className="btn btn-teal">
                      Pilih File dari perangkat anda
                    </button>
                    <div className="note">Gambar maksimal 300 KB</div>
                  </div>
                </div>
              </section>
            )}
          </Dropzone>
        </div>
        {(imagePreview || filesImage.length > 0) && (
          <div
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              padding: "8px",
              background: "#fafafa",
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Preview"
                onClick={() => setModalImage(imagePreview)}
                style={{
                  width: "160px",
                  height: "160px",
                  objectFit: "cover",
                  borderRadius: "6px",
                  border: "1px solid #d0d0d0",
                  cursor: "pointer",
                  display: "block",
                }}
              />
            )}
            {filesImage.length > 0 && (
              <div
                className="text-success"
                style={{
                  fontSize: "12px",
                  textAlign: "center",
                  wordBreak: "break-all",
                  maxWidth: "160px",
                }}
              >
                File terpilih: {filesImage[0].name}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Mutual exclusivity + anopheles rule for intervention options.
  const interventionDone = formik.values.intervention_done || [];
  const hasActualIntervention = interventionDone.some((v) =>
    actualInterventionValues.includes(v)
  );
  const hasBelumIntervention = interventionDone.includes(
    "Belum dilakukan Intervensi"
  );

  let renderedInterventionOptions = InterventionDoneOptions;
  if (hasActualIntervention) {
    renderedInterventionOptions = InterventionDoneOptions.filter(
      (opt) => opt.value !== "Belum dilakukan Intervensi"
    );
  } else if (hasBelumIntervention) {
    renderedInterventionOptions = InterventionDoneOptions.filter(
      (opt) => opt.value === "Belum dilakukan Intervensi"
    );
  }

  const disabledInterventionOptions = [];
  // (#4d) When no Anopheles larva found, Larvasidasi & Penebaran Ikan are not applicable.
  if (formik.values.anopheles_larva_found === "Tidak") {
    disabledInterventionOptions.push("Larvasidasi", "Penebaran Ikan");
  }

  return (
    <>
      <BreadcrumbNav
        items={[
          {
            label: "Kohort",
            onClick: () => changeMode(PageMode.view),
          },
          {
            label:
              pageMode === PageMode.add
                ? "Tambah Data Kohort"
                : "Edit Data Kohort",
          },
        ]}
      />
      <h1 className="page-title">Form Data Kohort</h1>
      <div className="form-grouping">
        <p
          className="warning"
          style={{ marginTop: "24px", marginBottom: "24px" }}
        >
          NOTE <span>*(WAJIB DIISI)</span>
        </p>

        {/* 1. Tanggal Kegiatan Intervensi */}
        <InputDate
          label={"Tanggal Kegiatan Intervensi"}
          name="intervention_date"
          placeholder={"Pilih Tanggal Intervensi"}
          dateFormat={"YYYY-MM-DD"}
          onChange={(e) => {
            const selectedDate = moment(e).format("YYYY-MM-DD");
            formik.handleChange("intervention_date")(selectedDate);
          }}
          onBlur={formik.handleBlur}
          value={formik.values.intervention_date}
          error={
            formik.errors.intervention_date && formik.touched.intervention_date
          }
          errorMessage={formik.errors.intervention_date}
          isRequired={true}
        />

        {/* 2. Nama Petugas/Tim */}
        <InputField
          label="Nama Petugas/Tim"
          name="officer_team"
          placeholder="Masukkan Nama Petugas/Tim"
          {...formik.getFieldProps("officer_team")}
          error={formik.errors.officer_team && formik.touched.officer_team}
          errorMessage={formik.errors.officer_team}
          isRequired={true}
        />

        {/* 3. Status Tempat Perindukan */}
        <InputSelect
          label="Status Tempat Perindukan"
          name="status"
          placeholder="Pilih Status Tempat Perindukan"
          data={StatusTpOptions}
          onChange={(e) => {
            const nextStatus = e ? e.value : "";
            if (nextStatus === "Tidak Ada") {
              formik.setValues({
                ...formik.values,
                status: nextStatus,
                activity_type: "",
                anopheles_larva_found: "",
                total_catch: "",
                total_larva: "",
                larva_phase: [],
                area_breeding_place: "",
                depth_breeding_place: "",
                distance_to_nearest_house: "",
                water_turbidity: "",
                plan_exist_breeding_place: "",
                rubbish_exist_breeding_place: "",
                trait_breeding_place: "",
                sun_exposure: "",
                brackish_water: "",
                running_water: "",
                fish_exist: "",
                intervention_done: [],
                other_intervention_done: "",
                other_belum_intervention: "",
                larvicide_entries: [],
                coordinate: "",
              });
              setFilesImageLarva([]);
              setImageLarvaPreview("");
            } else if (nextStatus === "Masih Ada") {
              const carryoverCoordinate =
                pageMode === PageMode.add && previousKohort?.coordinate
                  ? String(previousKohort.coordinate)
                  : formik.values.coordinate;

              formik.setValues({
                ...formik.values,
                status: nextStatus,
                tp_gone_reason: "",
                activity_type: "",
                coordinate: carryoverCoordinate,
              });
            } else {
              formik.setFieldValue("status", "");
              formik.setFieldValue("activity_type", "");
            }
          }}
          onBlur={formik.handleBlur}
          value={StatusTpOptions.find((x) => x.value === formik.values.status)}
          error={formik.errors.status && formik.touched.status}
          errorMessage={formik.errors.status}
          isRequired={true}
        />

        {/* Info Kepadatan Jentik Terakhir */}
        {formik.values.status === "Masih Ada" &&
          previousKohort &&
          Number(previousKohort.total_catch) > 0 && (
            <div
              style={{
                fontSize: "14px",
                color: "#4B5563",
                marginTop: "8px",
                marginBottom: "8px",
              }}
            >
              {`Kepadatan Jentik Terakhir: ${(
                (Number(previousKohort.total_larva || 0) /
                  Number(previousKohort.total_catch)) *
                100
              ).toFixed(2)} (kohort ${
                previousKohort.intervention_date
                  ? moment(previousKohort.intervention_date).format(
                      "YYYY-MM-DD"
                    )
                  : "-"
              })`}
            </div>
          )}

        {/* Branch: Tidak Ada */}
        {formik.values.status === "Tidak Ada" && (
          <>
            <InputSelect
              label="Alasan Tempat Perindukan Hilang"
              name="tp_gone_reason"
              placeholder="Pilih Alasan Tempat Perindukan Hilang"
              data={TpGoneReasonOptions}
              onChange={(e) =>
                formik.setFieldValue("tp_gone_reason", e ? e.value : "")
              }
              onBlur={formik.handleBlur}
              value={TpGoneReasonOptions.find(
                (x) => x.value === formik.values.tp_gone_reason
              )}
              error={
                formik.errors.tp_gone_reason && formik.touched.tp_gone_reason
              }
              errorMessage={formik.errors.tp_gone_reason}
              isRequired={true}
            />
            {renderBreedingImageDropzone()}
          </>
        )}

        {/* Branch: Masih Ada */}
        {formik.values.status === "Masih Ada" && (
          <>
            {/* 4. Jumlah Cidukan Yang Dilakukan */}
            <InputField
              label="Jumlah Cidukan Yang Dilakukan"
              name="total_catch"
              type="number"
              placeholder="Masukkan Jumlah Cidukan Yang Dilakukan"
              {...formik.getFieldProps("total_catch")}
              error={formik.errors.total_catch && formik.touched.total_catch}
              errorMessage={formik.errors.total_catch}
              isRequired={true}
            />

            {/* 5. Ditemukan Jentik Anopheles */}
            <InputSelect
              label="Ditemukan Jentik Anopheles"
              name="anopheles_larva_found"
              placeholder="Pilih Ya/Tidak"
              data={YesAndNoOptions}
              onChange={(e) => {
                const val = e ? e.value : "";
                formik.setFieldValue("anopheles_larva_found", val);
                if (val === "Tidak") {
                  formik.setFieldValue("total_larva", "0");
                  setFilesImageLarva([]);
                  setImageLarvaPreview("");
                } else {
                  formik.setFieldValue("total_larva", "");
                }
              }}
              onBlur={formik.handleBlur}
              value={YesAndNoOptions.find(
                (x) => x.value === formik.values.anopheles_larva_found
              )}
              error={
                formik.errors.anopheles_larva_found &&
                formik.touched.anopheles_larva_found
              }
              errorMessage={formik.errors.anopheles_larva_found}
              isRequired={true}
            />

            {/* 6. Jumlah Keseluruhan Jentik Dalam Seluruh Cidukan */}
            <InputField
              label="Jumlah Keseluruhan Jentik Dalam Seluruh Cidukan"
              name="total_larva"
              type="number"
              placeholder="Masukkan Jumlah Keseluruhan Jentik Dalam Seluruh Cidukan"
              {...formik.getFieldProps("total_larva")}
              readOnly={formik.values.anopheles_larva_found === "Tidak"}
              error={formik.errors.total_larva && formik.touched.total_larva}
              errorMessage={formik.errors.total_larva}
              isRequired={true}
            />

            {/* 7. Instar Jentik Yang Ditemukan */}
            <InputCheckbox
              label="Instar Jentik Yang Ditemukan"
              name="larva_phase"
              options={[
                { label: "Instar 1", value: "Instar 1" },
                { label: "Instar 2", value: "Instar 2" },
                { label: "Instar 3", value: "Instar 3" },
                { label: "Instar 4", value: "Instar 4" },
                { label: "Pupae", value: "Pupae" },
              ]}
              onChange={(e) => handleCheckboxChange(e, "larva_phase")}
              selectedOption={formik.values.larva_phase}
              disabled={formik.values.anopheles_larva_found === "Tidak"}
            />

            {/* 8. Luas Tempat Perindukan (m2) */}
            <InputField
              label="Luas Tempat Perindukan (m2)"
              name="area_breeding_place"
              type="number"
              isDecimal={true}
              placeholder="Masukkan Luas"
              {...formik.getFieldProps("area_breeding_place")}
              error={
                formik.errors.area_breeding_place &&
                formik.touched.area_breeding_place
              }
              errorMessage={formik.errors.area_breeding_place}
              isRequired={true}
            />

            {/* 9. Kedalaman Tempat Perindukan (m) */}
            <InputField
              label="Kedalaman Tempat Perindukan (m)"
              name="depth_breeding_place"
              type="number"
              isDecimal={true}
              placeholder="Masukkan Kedalaman"
              {...formik.getFieldProps("depth_breeding_place")}
              error={
                formik.errors.depth_breeding_place &&
                formik.touched.depth_breeding_place
              }
              errorMessage={formik.errors.depth_breeding_place}
              isRequired={true}
            />

            {/* 10. Jarak Tempat Perindukan Dari Rumah Terdekat (m) */}
            <InputField
              label="Jarak Tempat Perindukan Dari Rumah Terdekat (m)"
              name="distance_to_nearest_house"
              type="number"
              isDecimal={true}
              placeholder="Masukkan Jarak"
              {...formik.getFieldProps("distance_to_nearest_house")}
              error={
                formik.errors.distance_to_nearest_house &&
                formik.touched.distance_to_nearest_house
              }
              errorMessage={formik.errors.distance_to_nearest_house}
              isRequired={true}
            />

            {/* 11. Kekeruhan Air */}
            <InputSelect
              label="Kekeruhan Air"
              name="water_turbidity"
              placeholder="Pilih Kekeruhan Air"
              data={[
                { label: "Jernih", value: "Jernih" },
                { label: "Keruh", value: "Keruh" },
              ]}
              onChange={(e) =>
                formik.setFieldValue("water_turbidity", e ? e.value : "")
              }
              onBlur={formik.handleBlur}
              value={[
                { label: "Jernih", value: "Jernih" },
                { label: "Keruh", value: "Keruh" },
              ].find((x) => x.value === formik.values.water_turbidity)}
              error={
                formik.errors.water_turbidity && formik.touched.water_turbidity
              }
              errorMessage={formik.errors.water_turbidity}
              isRequired={true}
            />

            {/* 12. Adanya Tanaman Pada Permukaan Air */}
            <InputSelect
              label="Adanya Tanaman Pada Permukaan Air"
              name="plan_exist_breeding_place"
              placeholder="Pilih Ya/Tidak"
              data={YesAndNoOptions}
              onChange={(e) =>
                formik.setFieldValue(
                  "plan_exist_breeding_place",
                  e ? e.value : ""
                )
              }
              onBlur={formik.handleBlur}
              value={YesAndNoOptions.find(
                (x) => x.value === formik.values.plan_exist_breeding_place
              )}
              error={
                formik.errors.plan_exist_breeding_place &&
                formik.touched.plan_exist_breeding_place
              }
              errorMessage={formik.errors.plan_exist_breeding_place}
              isRequired={true}
            />

            {/* 13. Adanya Sampah Pada Permukaan Air */}
            <InputSelect
              label="Adanya Sampah Pada Permukaan Air"
              name="rubbish_exist_breeding_place"
              placeholder="Pilih Ya/Tidak"
              data={YesAndNoOptions}
              onChange={(e) =>
                formik.setFieldValue(
                  "rubbish_exist_breeding_place",
                  e ? e.value : ""
                )
              }
              onBlur={formik.handleBlur}
              value={YesAndNoOptions.find(
                (x) => x.value === formik.values.rubbish_exist_breeding_place
              )}
              error={
                formik.errors.rubbish_exist_breeding_place &&
                formik.touched.rubbish_exist_breeding_place
              }
              errorMessage={formik.errors.rubbish_exist_breeding_place}
              isRequired={true}
            />

            {/* 14. Sifat Tempat Perindukan */}
            <InputSelect
              label="Sifat Tempat Perindukan"
              name="trait_breeding_place"
              placeholder="Pilih Sifat"
              data={TraitBreedingOptions}
              onChange={(e) =>
                formik.setFieldValue("trait_breeding_place", e ? e.value : "")
              }
              onBlur={formik.handleBlur}
              value={TraitBreedingOptions.find(
                (x) => x.value === formik.values.trait_breeding_place
              )}
              error={
                formik.errors.trait_breeding_place &&
                formik.touched.trait_breeding_place
              }
              errorMessage={formik.errors.trait_breeding_place}
              isRequired={true}
            />

            {/* 15. Paparan Sinar Matahari */}
            <InputSelect
              label="Paparan Sinar Matahari"
              name="sun_exposure"
              placeholder="Pilih Ya/Tidak"
              data={YesAndNoOptions}
              onChange={(e) =>
                formik.setFieldValue("sun_exposure", e ? e.value : "")
              }
              onBlur={formik.handleBlur}
              value={YesAndNoOptions.find(
                (x) => x.value === formik.values.sun_exposure
              )}
              error={
                formik.errors.sun_exposure && formik.touched.sun_exposure
              }
              errorMessage={formik.errors.sun_exposure}
              isRequired={true}
            />

            {/* 16. Air Payau */}
            <InputSelect
              label="Air Payau"
              name="brackish_water"
              placeholder="Pilih Air Payau"
              data={BrackishWaterOptions}
              onChange={(e) =>
                formik.setFieldValue("brackish_water", e ? e.value : "")
              }
              onBlur={formik.handleBlur}
              value={BrackishWaterOptions.find(
                (x) => x.value === formik.values.brackish_water
              )}
              error={
                formik.errors.brackish_water && formik.touched.brackish_water
              }
              errorMessage={formik.errors.brackish_water}
              isRequired={true}
            />

            {/* 17. Air Mengalir */}
            <InputSelect
              label="Air Mengalir"
              name="running_water"
              placeholder="Pilih Ya/Tidak"
              data={YesAndNoOptions}
              onChange={(e) =>
                formik.setFieldValue("running_water", e ? e.value : "")
              }
              onBlur={formik.handleBlur}
              value={YesAndNoOptions.find(
                (x) => x.value === formik.values.running_water
              )}
              error={
                formik.errors.running_water && formik.touched.running_water
              }
              errorMessage={formik.errors.running_water}
              isRequired={true}
            />

            {/* 18. Ada Ikan */}
            <InputSelect
              label="Ada Ikan"
              name="fish_exist"
              placeholder="Pilih Ya/Tidak"
              data={YesAndNoOptions}
              onChange={(e) =>
                formik.setFieldValue("fish_exist", e ? e.value : "")
              }
              onBlur={formik.handleBlur}
              value={YesAndNoOptions.find(
                (x) => x.value === formik.values.fish_exist
              )}
              error={formik.errors.fish_exist && formik.touched.fish_exist}
              errorMessage={formik.errors.fish_exist}
              isRequired={true}
            />

            {/* Jenis Kegiatan */}
            {showActivityType && (
              <InputSelect
                label="Jenis Kegiatan"
                name="activity_type"
                placeholder="Pilih Jenis Kegiatan"
                data={KohortActivityTypeOptions}
                onChange={handleActivityTypeChange}
                onBlur={formik.handleBlur}
                value={KohortActivityTypeOptions.find(
                  (x) => x.value === formik.values.activity_type
                )}
                error={
                  formik.errors.activity_type && formik.touched.activity_type
                }
                errorMessage={formik.errors.activity_type}
                isRequired={true}
              />
            )}

            {/* 19. Intervensi Yang Telah Dilakukan Pada Tempat Perindukan (Larva Sources Management) */}
            <InputCheckbox
              label="Intervensi Yang Telah Dilakukan Pada Tempat Perindukan (Larva Sources Management)"
              name="intervention_done"
              options={renderedInterventionOptions}
              onChange={(e) => handleCheckboxChange(e, "intervention_done")}
              error={
                formik.errors.intervention_done &&
                formik.touched.intervention_done
              }
              errorMessage={formik.errors.intervention_done}
              onBlur={formik.handleBlur}
              selectedOption={formik.values.intervention_done}
              disabledOptions={disabledInterventionOptions}
              isRequired={true}
            />

            {formik.values.intervention_done.includes("Larvasidasi") && (
              <div
                className="larvicide-repeater-container"
                style={{ marginBottom: "20px" }}
              >
                {(formik.values.larvicide_entries || []).map((entry, index) => {
                  const entryErrors =
                    formik.errors.larvicide_entries?.[index];
                  const hasMultiple =
                    formik.values.larvicide_entries.length > 1;

                  return (
                    <div
                      key={index}
                      style={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        padding: "16px",
                        marginBottom: "16px",
                        background: "#fafafa",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "12px",
                        }}
                      >
                        <h6
                          style={{
                            margin: 0,
                            fontWeight: 600,
                            color: "#333",
                          }}
                        >
                          Larvasida {hasMultiple ? `#${index + 1}` : ""}
                        </h6>
                        {hasMultiple && (
                          <Button
                            type="button"
                            variant="danger"
                            size="small"
                            onClick={() => handleRemoveLarvicideRow(index)}
                          >
                            Hapus
                          </Button>
                        )}
                      </div>

                      {/* Jenis Larvasida */}
                      <InputSelect
                        label="Jenis Larvasida"
                        name={`larvicide_entries[${index}].type`}
                        placeholder="Pilih Jenis Larvasida"
                        data={LarvacideTypeOptions}
                        onChange={(e) => {
                          const val = e ? e.value : "";
                          handleLarvicideEntryChange(index, "type", val);
                        }}
                        onBlur={formik.handleBlur}
                        value={LarvacideTypeOptions?.find(
                          (item) =>
                            item.value ===
                            (entry.type === "BenzoylUreas"
                              ? "Benzoilurea"
                              : entry.type)
                        )}
                        error={Boolean(entryErrors?.type)}
                        errorMessage={entryErrors?.type}
                        isRequired={pageMode === PageMode.add}
                      />

                      {/* Bentuk Larvasida */}
                      <InputSelect
                        label="Bentuk Larvasida"
                        name={`larvicide_entries[${index}].form`}
                        placeholder="Pilih Bentuk Larvasida"
                        data={LarvacideFormOptions}
                        onChange={(e) => {
                          const val = e ? e.value : "";
                          handleLarvicideEntryChange(index, "form", val);
                        }}
                        onBlur={formik.handleBlur}
                        value={LarvacideFormOptions?.find(
                          (item) => item.value === entry.form
                        )}
                        error={Boolean(entryErrors?.form)}
                        errorMessage={entryErrors?.form}
                        isRequired={pageMode === PageMode.add}
                      />

                      {/* Bentuk Lainnya */}
                      {entry.form === "Lainnya" && (
                        <InputField
                          label="Bentuk Lainnya"
                          name={`larvicide_entries[${index}].form_other`}
                          placeholder="Masukkan Bentuk Lainnya"
                          value={entry.form_other || ""}
                          onChange={(e) =>
                            handleLarvicideEntryChange(
                              index,
                              "form_other",
                              e.target.value
                            )
                          }
                          onBlur={formik.handleBlur}
                          error={Boolean(entryErrors?.form_other)}
                          errorMessage={entryErrors?.form_other}
                          isRequired={true}
                        />
                      )}

                      <div className="row">
                        <div className="col-sm-6">
                          <InputField
                            label="Jumlah Larvasida"
                            name={`larvicide_entries[${index}].amount`}
                            placeholder="0"
                            type="number"
                            isDecimal={true}
                            value={entry.amount ?? ""}
                            onChange={(e) =>
                              handleLarvicideEntryChange(
                                index,
                                "amount",
                                e.target.value
                              )
                            }
                            onBlur={formik.handleBlur}
                            error={Boolean(entryErrors?.amount)}
                            errorMessage={entryErrors?.amount}
                            isRequired={pageMode === PageMode.add}
                          />
                        </div>
                        <div className="col-sm-6">
                          <InputSelect
                            label="Satuan"
                            name={`larvicide_entries[${index}].unit`}
                            placeholder="Pilih Satuan"
                            data={UnitOptions}
                            onChange={(e) => {
                              const val = e ? e.value : "";
                              handleLarvicideEntryChange(index, "unit", val);
                            }}
                            onBlur={formik.handleBlur}
                            value={UnitOptions?.find(
                              (item) => item.value === entry.unit
                            )}
                            error={Boolean(entryErrors?.unit)}
                            errorMessage={entryErrors?.unit}
                            isRequired={pageMode === PageMode.add}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                <Button
                  type="button"
                  variant="secondary"
                  size="normal"
                  onClick={handleAddLarvicideRow}
                >
                  + Tambah Larvasida
                </Button>
              </div>
            )}

            {formik.values.intervention_done.includes("Lainnya") && (
              <InputField
                label={"Intervensi Lainnya"}
                placeholder={"Intervensi Lainnya"}
                name={"other_intervention_done"}
                {...formik.getFieldProps("other_intervention_done")}
                error={
                  formik.errors.other_intervention_done &&
                  formik.touched.other_intervention_done
                }
                errorMessage={formik.errors.other_intervention_done}
              />
            )}

            {formik.values.intervention_done.includes(
              "Belum dilakukan Intervensi"
            ) && (
              <InputField
                label={"Rencana Intervensi atau Rekomendasi Intervensi"}
                placeholder={"Rencana Intervensi atau Rekomendasi Intervensi"}
                name={"other_belum_intervention"}
                {...formik.getFieldProps("other_belum_intervention")}
                error={
                  formik.errors.other_belum_intervention &&
                  formik.touched.other_belum_intervention
                }
                errorMessage={formik.errors.other_belum_intervention}
              />
            )}

            {/* 20. Lokasi Koordinat (maps + coordinate) */}
            <div className="form-group">
              <label className="image">Lokasi (Peta)</label>
              <MapsComponents
                coordinate={formik.values.coordinate}
                onChangeLocation={(address, coordinate) => {
                  handleMapChange(address, coordinate);
                }}
              />
            </div>
            <InputField
              placeholder={"Titik koordinat akan muncul"}
              name="coordinate"
              value={formik.values.coordinate}
              readOnly={true}
              error={formik.errors.coordinate && formik.touched.coordinate}
              errorMessage={formik.errors.coordinate}
            />

            {/* 21. Ambil Gambar Tempat Perindukan */}
            {renderBreedingImageDropzone()}

            {/* 22. Foto Jentik Yang Anda Temukan */}
            {formik.values.anopheles_larva_found !== "Tidak" && (
              <div className="form-group">
                <label className="image">
                  Foto Jentik Yang Anda Temukan (Opsional)
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <Dropzone
                      maxFiles={1}
                      accept="image/*"
                      onDrop={(acceptedFiles, fileRejections) => {
                        if (fileRejections.length > 0) {
                          if (fileRejections[0].errors[0].code === "file-too-large") {
                            toast.error("Ukuran file tidak boleh lebih dari 300 Kb");
                          } else if (
                            fileRejections[0].errors[0].code === "file-invalid-type"
                          ) {
                            toast.error("Format file tidak sesuai");
                          }
                        }
                        if (acceptedFiles.length > 0) {
                          setFilesImageLarva(acceptedFiles);
                          setImageLarvaPreview(URL.createObjectURL(acceptedFiles[0]));
                        }
                      }}
                      maxSize={1024 * 300}
                    >
                      {({ getRootProps, getInputProps }) => (
                        <section
                          className="container"
                          style={{ maxWidth: "360px", margin: 0, padding: 0 }}
                        >
                          <div
                            {...getRootProps({
                              className: "dropzone",
                              style: { padding: "24px 16px" },
                            })}
                          >
                            <input {...getInputProps()} />
                            <div className="dz-message">
                              <div className="icon">
                                <i className="flaticon-file"></i>
                              </div>
                              <h5>Upload foto jentik</h5>
                              <div className="note">Drag & Drop or</div>
                              <button type="button" className="btn btn-teal">
                                Pilih File dari perangkat anda
                              </button>
                              <div className="note">Gambar maksimal 300 KB</div>
                            </div>
                          </div>
                        </section>
                      )}
                    </Dropzone>
                  </div>
                  {(imageLarvaPreview || filesImageLarva.length > 0) && (
                    <div
                      style={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        padding: "8px",
                        background: "#fafafa",
                        display: "inline-flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {imageLarvaPreview && (
                        <img
                          src={imageLarvaPreview}
                          alt="Preview"
                          onClick={() => setModalImage(imageLarvaPreview)}
                          style={{
                            width: "160px",
                            height: "160px",
                            objectFit: "cover",
                            borderRadius: "6px",
                            border: "1px solid #d0d0d0",
                            cursor: "pointer",
                            display: "block",
                          }}
                        />
                      )}
                      {filesImageLarva.length > 0 && (
                        <div
                          className="text-success"
                          style={{
                            fontSize: "12px",
                            textAlign: "center",
                            wordBreak: "break-all",
                            maxWidth: "160px",
                          }}
                        >
                          File terpilih: {filesImageLarva[0].name}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <div className="button_action">
          <Button
            variant={"tertiary"}
            size={"normal"}
            onClick={() => changeMode(PageMode.view)}
          >
            Batalkan
          </Button>
          <Button
            type={"submit"}
            variant={"primary"}
            size={"normal"}
            onClick={formik.handleSubmit}
            isDisabled={formik.isSubmitting}
          >
            Simpan Data
          </Button>
        </div>
      </div>

      {modalImage && (
        <div
          onClick={() => setModalImage("")}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
          >
            <button
              type="button"
              onClick={() => setModalImage("")}
              style={{
                position: "absolute",
                top: "-12px",
                right: "-12px",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                border: "none",
                background: "#fff",
                color: "#333",
                cursor: "pointer",
                fontSize: "18px",
                lineHeight: "32px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
              }}
              aria-label="Tutup"
            >
              &times;
            </button>
            <img
              src={modalImage}
              alt="Preview besar"
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                objectFit: "contain",
                borderRadius: "8px",
                display: "block",
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FormComponent;
